import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Modal,
  Typography,
  Alert,
  Autocomplete,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import api from "../axiosConfig";

const initialFormData = {
  Category: "",
  Product_Name: "",
  Units: "",
  Measurement: "",
  Weightage: "",
  Standard_Unit: "",
  Storage_Cat: "",
  Sub_Category: "",
  Tax_Slab: "",
  Pieces_Per_Pack: "",
  On_Pack_Category: "",
  RM_Weightage: "",
  SKU_ID: "",
};

const ProductMaster = ({ open }) => {
  const [rowData, setRowData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storageCats, setStorageCats] = useState([]);
  const [sub_category, setsub_category] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");

  const fetchProduct = async () => {
    try {
      const response = await api.get("/api/productdetails");
      console.log(response.data);

      setRowData(response.data);
      setFilteredData(response.data);
    } catch (err) {
      setError("Failed to fetch products. Please try again later.");
      console.error("Error fetching products:", err);
    }
  };

  useEffect(() => {
    fetchProduct();

    // Fetch categories
    api
      .get("/api/getcategorydetails")
      .then((response) => {
        // Assuming the API returns an array of category objects with a name property
        setCategories(response.data);
      })
      .catch((error) => {
        console.error("Error fetching categories: ", error);
      });

    // Fetch storage categories
    api
      .get("/api/getstoragecategorydetails")
      .then((response) => {
        setStorageCats(response.data);
      })
      .catch((error) => {
        console.error("Error fetching storage categories: ", error);
      });
      api
      .get("/api/getsubcategorydetails")
      .then((response) => {
        setsub_category(response.data);
      })
      .catch((error) => {
        console.error("Error fetching storage categories: ", error);
      });
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Update the field and calculate Weightage if necessary
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };

      if (name === "Units" || name === "Measurement") {
        const { Units, Measurement } = updatedData;
        if (Units && Measurement) {
          let weightageValue = 0;

          switch (Measurement) {
            case "g":
            case "kg":
            case "ml":
              weightageValue = Units / 1000;
              break;
            case "U":
              weightageValue = Units * 1;
              break;
            default:
              weightageValue = 0;
          }

          updatedData.Weightage = weightageValue;
        }
      }

      return updatedData;
    });
  };

  // Search filter
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) {
      const filtered = rowData.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(value.toLowerCase())
        )
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(rowData);
    }
  };

  const openModal = (data = null) => {
    if (data) {
      console.log("data", data);

      // For editing, find the full category and storage category objects
      const categoryObj =
        categories.find((cat) => cat.Category === data.Category) || null;
      const storageCatObj =
        storageCats.find(
          (storage) => storage.Storage_Cat === data.Storage_Cat
        ) || null;
      const subCatObj =
        sub_category.find(
          (storage) => storage.Sub_Category === data.Sub_Category
        ) || null;
      setFormData({
        ...data,
        Category: categoryObj,
        Storage_Cat: storageCatObj,
        Sub_Category:subCatObj
      });
      setIsEditing(true);
    } else {
      setFormData(initialFormData);
      setIsEditing(false);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormData);
    setIsEditing(false);
    setError("");
  };

  const handleSubmit = async () => {
    try {
      // Prepare the data for submission
      const submissionData = {
        body: {
          ...formData,
          Category: formData.Category?.Category || "",
          Storage_Cat: formData.Storage_Cat?.Storage_Cat || "",
          Sub_Category: formData.Sub_Category?.Sub_Category || "",
        },
      };

      if (isEditing) {
        const formatData = {
          body: {
            ...Object.fromEntries(
              Object.entries(formData).map(([key, value]) => [
                key,
                String(value ?? ""),
              ])
            ),
            Category: formData.Category?.Category || "",
            Storage_Cat: formData.Storage_Cat?.Storage_Cat || "",
            Sub_Category: formData.Sub_Category?.Sub_Category || "",
          },
        };
        console.log(formatData);

        const response = await api.post(
          "/api/productdetailsupdate",
          formatData
        );
        console.log(response.data);

        if (response && response.data.Status === "success") {
          alert("Product Updated Successfully");

          const updatedProduct = response.data.data_updated[0];
          setRowData((prevRowData) =>
            prevRowData.map((row) =>
              row.id === updatedProduct.id ? updatedProduct : row
            )
          );
        } else {
          alert("Failed to update product");
        }
      } else {
        const response = await api.post(
          "/api/productdetailsinsert",
          submissionData
        );
        console.log(response.data);

        if (response && response.data.Status === "success") {
          alert("Product Added Successfully");
          const newProduct = response.data.data_updated[0];
          setRowData((prevRowData) => [...prevRowData, newProduct]);
        } else {
          alert("Failed to add product");
        }
      }
      closeModal();
    } catch (err) {
      setError(
        isEditing ? "Failed to update product" : "Failed to add product"
      );
      console.error("Error:", err);
    }
  };

  // Rest of your columns definition remains the same...
  const columns = [
    // { headerName: "ID", field: "id", sortable: true, filter: true },
    { headerName: "Product Code", field: "SKU_ID", sortable: true, filter: true },
    // {
    //   headerName: "Product Code",
    //   field: "Product_Code",
    //   sortable: true,
    //   filter: true,
    // },
    {
      headerName: "Product Name",
      field: "Product_Name",
      sortable: true,
      filter: true,
    },
    { headerName: "Category", field: "Category", sortable: true, filter: true },
    {
      headerName: "Sub Category",
      field: "Sub_Category",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Storage Category",
      field: "Storage_Cat",
      sortable: true,
      filter: true,
    },
 
    { headerName: "Units", field: "Units", sortable: true, filter: true },
    {
      headerName: "Measurement",
      field: "Measurement",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Weightage",
      field: "Weightage",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Standard Unit",
      field: "Standard_Unit",
      sortable: true,
      filter: true,
    },
  
    { headerName: "Tax Slab", field: "Tax_Slab", sortable: true, filter: true },
    {
      headerName: "Pieces Per Pack",
      field: "Pieces_Per_Pack",
      sortable: true,
      filter: true,
    },
    {
      headerName: "On Pack Category",
      field: "On_Pack_Category",
      sortable: true,
      filter: true,
    },
    {
      headerName: "RM Weightage",
      field: "RM_Weightage",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params) => (
        <Button
          variant="outlined"
          size="small"
          sx={{
            fontSize: "12px",
            color: "#1A1A1A",
            border: "1px solid #707070",
            background: "#fff",
          }}
          onClick={() => openModal(params.data)}
        >
          <EditIcon sx={{ fontSize: "13px", marginRight: "5px" }} />
          Edit
        </Button>
      ),
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    resizable: true,
  };

  const productContent = {
    Measurement: ["g", "kg", "ml", "U"],
    Storage_Cat: ["Ambient", "Chilled", "Frozen"],
    Sub_Category: ['Idly Dosa Batter','Malabar Parota','Wheat Parota','Chapati','Other Batter','Idiyappam','Cup Curd','Paneer','Coffee BOLD','Coffee INTENSE','Grated Coconut','Tender Coconut','Pouch Curd','Coffee Strong','Fresh Noodles','Wheat Noodles','Sandwich White Bread','Wheat Bread','Ghee','Coffee Instant','Frozen Fruit','Butter Stick','Chutney','Paratha','Honey','Frozen Batter','Phulka','Health Mixes','Snacks','Spices','Mallige Idly Batter','Multigrain Idly Batter','Ulavacha','Sambar'],//["Ambient", "Idly Dosa Batter", "Paratha", "Malabar Parota"],
    On_Pack_Category: ["Regular", "Extra", "Price Off", "Sample"],
  };

  return (
    <div className="ag-table-container"
    style={{ marginLeft: open ? "240px" : "0px" }}>
      {/* <Container maxWidth="xl"> */}
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => openModal()}
            sx={{
              fontSize: "12px",
              background: "#16a695",
            }}
            size="small"
          >
            <AddIcon sx={{ fontSize: "13px", marginRight: "5px" }} />
            Add Product
          </Button>


          {/* <TextField
            variant="outlined"
            placeholder="Search"
            value={searchValue}
            onChange={handleSearch}
            size="small"
          /> */}
        </Box>

        <div
         className="ag-theme-quartz"
          style={{
            width: "100%",
            height: "calc(100dvh - 190px)",
            fontSize: "12px",
          }}
        >
          <AgGridReact
            rowData={filteredData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            pagination={false}
            paginationPageSize={10}
          />
        </div>

        <Modal open={isModalOpen} onClose={closeModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 1000,
              maxHeight: "80vh",
              overflowY: "auto",
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {isEditing ? "Edit Product" : "Add Product"}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Box sx={{ display: "flex", gap: "20px" }}>
                <TextField
                  name="SKU_ID"
                  label="SKU ID"
                  variant="outlined"
                  fullWidth
                  value={formData.SKU_ID || ""}
                  onChange={handleInputChange}
                  sx={{ width: "300px" }}
                  InputProps={{
                    readOnly: isEditing,
                  }}
                />

                <TextField
                  name="Product_Name"
                  label="Product Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.Product_Name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, Product_Name: e.target.value })
                  }
                />
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.Category || ""}
                  value={formData.Category || null}
                  fullWidth
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, Category: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      required
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  options={storageCats}
                  getOptionLabel={(option) => option.Storage_Cat || ""}
                  value={formData.Storage_Cat || null}
                  fullWidth
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, Storage_Cat: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Storage Category" fullWidth />
                  )}
                />
              </Box>

              {/* Row 2 */}
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Autocomplete
                  options={productContent.Measurement} // Use options from productContent
                  getOptionLabel={(option) => option}
                  value={formData.Measurement || null}
                  fullWidth
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      Measurement: newValue, // Update Measurement field
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Measurement" fullWidth />
                  )}
                />
                <TextField
                  name="Units"
                  label="Units"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.Units || ""}
                  onChange={handleInputChange}
                />
                <TextField
                  name="Weightage"
                  label="Weightage"
                  variant="outlined"
                  fullWidth
                  value={formData.Weightage || ""}
                  onChange={handleInputChange}
                  InputProps={{
                    readOnly: true, // Make the field read-only
                  }}
                />
              </Box>

              {/* Row 3 */}
              <Box sx={{ display: "flex", gap: "20px" }}>
                <TextField
                  name="Standard_Unit"
                  label="Standard Unit"
                  variant="outlined"
                  fullWidth
                  value={formData.Standard_Unit || ""}
                  onChange={handleInputChange}
                />
                <Autocomplete
                  options={sub_category}
                  getOptionLabel={(option) => option.Sub_Category}
                  value={formData.Sub_Category || null}
                  fullWidth
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, Sub_Category: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Sub Category" fullWidth />
                  )}
                />

                <TextField
                  name="Tax_Slab"
                  label="Tax Slab"
                  variant="outlined"
                  fullWidth
                  value={formData.Tax_Slab || ""}
                  onChange={handleInputChange}
                />
              </Box>

              {/* Row 4 */}
              <Box sx={{ display: "flex", gap: "20px" }}>
                <TextField
                  name="Pieces_Per_Pack"
                  label="Pieces Per Pack"
                  variant="outlined"
                  fullWidth
                  value={formData.Pieces_Per_Pack || ""}
                  onChange={handleInputChange}
                />
                <Autocomplete
                  options={productContent.On_Pack_Category}
                  getOptionLabel={(option) => option}
                  value={formData.On_Pack_Category || null}
                  fullWidth
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, On_Pack_Category: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="On Pack Category" fullWidth />
                  )}
                />
                <TextField
                  name="RM_Weightage"
                  label="RM Weightage"
                  variant="outlined"
                  fullWidth
                  value={formData.RM_Weightage || ""}
                  onChange={handleInputChange}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!formData.Product_Name || !formData.Category}
                >
                  {isEditing ? "Update" : "Add"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      {/* </Container> */}
    </div>
  );
};

export default ProductMaster;
